import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { Item } from '@/types/models'
import { cn } from '@/lib/utils'

interface Props {
  /** 선택된 품목 표시 라벨 ('코드 품명'). 라인에 이미 있으므로 상세조회 불필요. */
  label?: string
  onChange: (it: Item) => void
  placeholder?: string
}

/** 출고/입고 라인 전용 인라인 품목 자동완성.
 *  모달 없이 입력창에서 바로 타이핑 → 드롭다운 → ↑↓/Enter/클릭 선택.
 *  실제 <input> 이라 한글 IME 도 정상 동작. 그리드(rowKeyDown) 방향키 이동과
 *  충돌하지 않도록, 드롭다운이 열려 결과가 있을 때만 ↑↓/Enter/Esc 를 가로챈다.
 *
 *  드롭다운은 body 로 portal(position:fixed) 렌더한다 — 라인 패널 래퍼의
 *  overflow(-x-auto) 가 세로까지 clip 해서 결과가 첫 줄만 보이던 문제를 피하기 위함. */
export function ItemCombo({ label = '', onChange, placeholder = '품목 선택...' }: Props) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // 드롭다운 위치(입력창 기준, viewport 좌표). portal + fixed 라 스크롤/리사이즈 시 재계산.
  // 아래 공간 부족 시 위로 펼치고(top/bottom 택1), 가용 높이에 맞춰 maxH 설정 → 내부 스크롤.
  const [pos, setPos] = useState<
    { left: number; width: number; top?: number; bottom?: number; maxH: number } | null
  >(null)

  const { data: results = [] } = useQuery({
    queryKey: ['items-combo', q],
    queryFn: () => api<Item[]>(`/api/items?q=${encodeURIComponent(q.trim())}`),
    enabled: open && q.trim().length > 0,
    staleTime: 30_000,
  })

  const place = useCallback(() => {
    const el = wrapRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 8
    const DESIRED = 288                                   // 최대 높이(max-h-72)
    const spaceBelow = window.innerHeight - r.bottom - margin
    const spaceAbove = r.top - margin
    if (spaceBelow >= 160 || spaceBelow >= spaceAbove) {  // 아래로 펼침
      setPos({ left: r.left, width: r.width, top: r.bottom + 2, maxH: Math.min(DESIRED, spaceBelow) })
    } else {                                              // 위로 펼침(아래 공간 부족)
      setPos({ left: r.left, width: r.width, bottom: window.innerHeight - r.top + 2, maxH: Math.min(DESIRED, spaceAbove) })
    }
  }, [])

  // 열려 있는 동안 위치 추적 — 모달 본문 스크롤(capture)·리사이즈 모두 반영.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  useEffect(() => { setActive(0) }, [q, open])
  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector<HTMLElement>(`[data-combo-idx="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [active, open, results.length])

  function select(it: Item) {
    onChange(it)
    setOpen(false)
    setQ('')
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (open && results.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault(); e.stopPropagation()
        setActive((i) => Math.min(i + 1, results.length - 1)); return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault(); e.stopPropagation()
        setActive((i) => Math.max(i - 1, 0)); return
      }
      if (e.key === 'Enter') {
        const it = results[active]
        if (it) { e.preventDefault(); e.stopPropagation(); select(it) }
        return
      }
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); return }
    }
    // 그 외(닫힘 상태 포함) → 그리드 방향키 이동(rowKeyDown)으로 흘려보냄
  }

  const showList = open && results.length > 0 && pos

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        value={open ? q : label}
        placeholder={placeholder}
        onFocus={(e) => e.currentTarget.select()}   // 라벨 전체 선택 → 타이핑 시 대체
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 120)}   // 드롭다운 클릭 여유
        className={cn('w-full px-2 py-1 text-sm border border-slate-300 rounded',
          !label && !open && 'placeholder:text-slate-400')}
      />
      {showList && createPortal(
        <div ref={listRef}
             style={{ left: pos.left, top: pos.top, bottom: pos.bottom, minWidth: pos.width, maxHeight: pos.maxH }}
             className="fixed z-[60] w-max max-w-[min(92vw,44rem)] overflow-y-auto
                        bg-white border border-slate-200 rounded-lg shadow-lg">
          {results.map((it, i) => (
            <button
              key={it.id}
              type="button"
              data-combo-idx={i}
              onMouseDown={(e) => { e.preventDefault(); select(it) }}
              onMouseEnter={() => setActive(i)}
              className={cn('w-full text-left px-3 py-1.5 text-sm border-b border-slate-100 whitespace-nowrap',
                i === active ? 'bg-blue-100' : 'hover:bg-blue-50')}
            >
              <span className="font-mono text-xs text-slate-500 tabular-nums mr-2">{it.code}</span>
              <span>{it.name}</span>
              {it.spec && <span className="text-xs text-slate-400 ml-2">{it.spec}</span>}
              {it.unit_out && (
                <span className="text-xs text-slate-500 ml-2 px-1.5 py-0.5 bg-slate-100 rounded">{it.unit_out}</span>
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}
